<script setup>
import { computed } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import TemplatePreview from 'dashboard/components-next/whatsapp/TemplatePreview.vue';
import { useMessageContext } from '../provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import {
  findTemplateInInbox,
  buildTemplateViewModel,
} from 'dashboard/helper/templateHelper';

// Timeline bubble for a sent WhatsApp template. The message carries the filled
// values (camelCased: processedParams.header.mediaUrl, …) + the rendered body
// in `content`; the template definition (button labels / footer / header
// structure) comes from the inbox. Combine both into the shared TemplatePreview.

const {
  content,
  additionalAttributes,
  templateParams: topTemplateParams,
  templateComponents,
  inboxId,
} = useMessageContext();
const inboxGetter = useMapGetter('inboxes/getInbox');

// Server-fetched messages nest it under additional_attributes; optimistic
// (just-sent) messages carry it at the top level.
const templateParams = computed(
  () =>
    additionalAttributes.value?.templateParams || topTemplateParams?.value || {}
);

// Prefer the definition the backend embedded on the message; fall back to the
// inbox's templates (e.g. optimistic just-sent messages).
const definition = computed(() => {
  if (templateComponents?.value?.length) {
    return { components: templateComponents.value };
  }
  return findTemplateInInbox(
    inboxGetter.value(inboxId.value),
    templateParams.value.name,
    templateParams.value.language
  );
});

const viewModel = computed(() =>
  buildTemplateViewModel({
    definition: definition.value,
    processedParams: templateParams.value.processedParams,
    renderedBody: content.value,
    name: templateParams.value.name,
  })
);
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="whatsapp-template">
    <TemplatePreview :view-model="viewModel" />
  </BaseBubble>
</template>
